<template>
  <div class="pm-root">

    <!-- ── HEADER ── -->
    <div class="pm-header">
      <div class="pm-header-left">
        <button class="pm-back-btn" @click="router.back()">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Dashboard
        </button>
        <div class="pm-header-child">
          <MTAvatar :studentId="childId" size="lg" />
          <div>
            <h1 class="pm-child-name">{{ childName }}</h1>
            <div class="pm-child-meta">
              <span class="pm-track-badge">{{ trackLabel }}</span>
              <span class="pm-last-active" :class="lastActiveClass">{{ lastActiveText }}</span>
            </div>
          </div>
        </div>
      </div>
      <button class="pm-open-btn" @click="openStudentView">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <polygon points="5 3 19 12 5 21 5 3"/>
        </svg>
        Open Student View
      </button>
    </div>

    <!-- ── HERO STATS (4 meaningful metrics) ── -->
    <div class="pm-hero">
      <div class="pm-hero-stat">
        <div class="pm-hero-icon pm-icon-purple">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
        </div>
        <div>
          <div class="pm-hero-val-row">
            <span class="pm-hero-val">{{ topicsSecure }}</span>
            <span v-if="secureGainThisWeek > 0" class="pm-hero-trend-pill">+{{ secureGainThisWeek }} this week</span>
          </div>
          <div class="pm-hero-lbl">Topics Secure+</div>
        </div>
      </div>
      <div class="pm-hero-divider"></div>
      <div class="pm-hero-stat">
        <div class="pm-hero-icon pm-icon-blue">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
          </svg>
        </div>
        <div>
          <div class="pm-hero-val">{{ topicsAttempted }}</div>
          <div class="pm-hero-lbl">Topics attempted</div>
        </div>
      </div>
      <div class="pm-hero-divider"></div>
      <div class="pm-hero-stat">
        <div class="pm-hero-icon pm-icon-green">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
          </svg>
        </div>
        <div>
          <div class="pm-hero-val">{{ lessonsCompleted }}</div>
          <div class="pm-hero-lbl">Lessons completed</div>
        </div>
      </div>
      <div class="pm-hero-divider"></div>
      <div class="pm-hero-stat">
        <div class="pm-hero-icon pm-icon-orange">
          <span style="font-size:20px;line-height:1;">🔥</span>
        </div>
        <div>
          <div class="pm-hero-val">{{ streak }}</div>
          <div class="pm-hero-lbl">Day streak</div>
        </div>
      </div>
    </div>

    <!-- ── MAIN GRID: Band Overview + Weekly Activity ── -->
    <div class="pm-grid-2">

      <!-- Band distribution -->
      <div class="pm-card pm-band-card">
        <h2 class="pm-card-title">Skill Levels</h2>
        <p class="pm-card-sub">How many topics {{ childName }} has reached at each level</p>

        <div class="pm-band-bar-wrap">
          <div class="pm-band-bar">
            <div
              v-for="band in BANDS"
              :key="band.key"
              class="pm-band-segment"
              :style="{ width: bandBarWidth(band.key) + '%', background: band.color }"
              :title="`${band.label}: ${bandCounts[band.key]} topics`"
            ></div>
          </div>
          <div class="pm-band-total">{{ totalTrackedTopics }} topics tracked</div>
        </div>

        <div class="pm-band-legend">
          <div v-for="band in BANDS" :key="band.key" class="pm-band-item">
            <div class="pm-band-dot" :style="{ background: band.color }"></div>
            <div class="pm-band-info">
              <div class="pm-band-count">{{ bandCounts[band.key] }}</div>
              <div class="pm-band-name">{{ band.label }}</div>
            </div>
          </div>
        </div>

        <div class="pm-insight" v-if="totalTrackedTopics > 0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {{ insightText }}
        </div>
      </div>

      <!-- 7-day activity -->
      <div class="pm-card pm-activity-card">
        <h2 class="pm-card-title">Last 7 Days</h2>
        <p class="pm-card-sub">Daily learning activity this week</p>

        <div class="pm-week-grid">
          <div v-for="day in weekActivity" :key="day.date" class="pm-day-col">
            <div class="pm-day-label">{{ day.dayLabel }}</div>
            <div class="pm-day-num">{{ day.dayNum }}</div>
            <div class="pm-day-dots">
              <div v-if="day.hasLesson"     class="pm-day-dot pm-dot-lesson"     title="Lesson"></div>
              <div v-if="day.hasRevision"   class="pm-day-dot pm-dot-revision"   title="Revision"></div>
              <div v-if="day.hasDiagnostic" class="pm-day-dot pm-dot-diagnostic" title="Skills Check"></div>
              <div v-if="!day.count"        class="pm-day-dot pm-dot-none"></div>
            </div>
            <div class="pm-day-count" :class="{ 'pm-day-count-active': day.count > 0 }">
              {{ day.count > 0 ? day.count : '–' }}
            </div>
          </div>
        </div>

        <div class="pm-week-legend">
          <div class="pm-wleg-item"><div class="pm-wleg-dot pm-dot-lesson"></div> Lesson</div>
          <div class="pm-wleg-item"><div class="pm-wleg-dot pm-dot-revision"></div> Revision</div>
          <div class="pm-wleg-item"><div class="pm-wleg-dot pm-dot-diagnostic"></div> Skills Check</div>
        </div>

        <!-- Updated summary: sessions + lessons + week-on-week trend -->
        <div class="pm-week-summary">
          <div class="pm-wsum-item">
            <span class="pm-wsum-val">{{ thisWeekSessions }}</span>
            <span class="pm-wsum-lbl">sessions</span>
          </div>
          <div class="pm-wsum-sep"></div>
          <div class="pm-wsum-item">
            <span class="pm-wsum-val">{{ thisWeekLessons }}</span>
            <span class="pm-wsum-lbl">lessons</span>
          </div>
          <div class="pm-wsum-sep"></div>
          <div class="pm-wsum-item">
            <span class="pm-wsum-val" :class="secureTrendClass">{{ secureTrendDisplay }}</span>
            <span class="pm-wsum-lbl">Secure+ vs last week</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ── FOCUS AREAS ── -->
    <div v-if="focusAreas.length" class="pm-card" style="margin-bottom: 16px;">
      <h2 class="pm-card-title">Focus Areas</h2>
      <p class="pm-card-sub">Topics where {{ childName }} needs the most practice</p>

      <div class="pm-focus-grid">
        <div v-for="area in focusAreas" :key="area.sgId" class="pm-focus-item">
          <div class="pm-focus-band-strip" :style="{ background: bandColor(area.band) }"></div>
          <div class="pm-focus-body">
            <div class="pm-focus-name">{{ area.name }}</div>
            <div class="pm-focus-lesson" v-if="area.lessonTitle">{{ area.lessonTitle }}</div>
          </div>
          <div class="pm-focus-badge" :class="`pm-focus-badge-${area.band}`">{{ capitalize(area.band) }}</div>
        </div>
      </div>
    </div>

    <!-- ── PROGRESS TREND ── -->
    <div v-if="totalTrackedTopics > 0" class="pm-card" style="margin-bottom: 16px;">
      <h2 class="pm-card-title">Progress Trend</h2>
      <p class="pm-card-sub">Topics at Secure+ reached or practised each week</p>

      <div class="pm-trend-weeks">
        <div v-for="week in weeklyTrend" :key="week.label" class="pm-trend-week-col">
          <div class="pm-trend-bar-wrap">
            <div
              class="pm-trend-bar-fill"
              :style="{ height: week.heightPct + '%' }"
              :class="week.isCurrent ? 'pm-trend-bar-current' : 'pm-trend-bar-past'"
            ></div>
          </div>
          <div class="pm-trend-count" :class="week.isCurrent ? 'pm-trend-count-current' : ''">
            {{ week.count }}
          </div>
          <div class="pm-trend-label">{{ week.label }}</div>
        </div>
      </div>

      <div class="pm-trend-footnote">
        Each bar = topics at Secure+ that were actively practised that week.
        <span v-if="overallTrendText" :class="overallTrendClass"> {{ overallTrendText }}</span>
      </div>
    </div>

    <!-- ── MODULE PROGRESS ── -->
    <div class="pm-card" style="margin-bottom: 16px;">
      <h2 class="pm-card-title">Progress by Module</h2>
      <p class="pm-card-sub">Topic band distribution across each module</p>

      <div class="pm-modules" v-if="moduleProgress.length">
        <div v-for="(mod, i) in moduleProgress" :key="mod.id" class="pm-module-row">
          <div class="pm-module-icon" :style="{ background: moduleColors[i % moduleColors.length].bg, color: moduleColors[i % moduleColors.length].fg }">
            {{ moduleEmoji(i) }}
          </div>
          <div class="pm-module-body">
            <div class="pm-module-header">
              <span class="pm-module-name">{{ mod.title }}</span>
              <span class="pm-module-lessons">{{ mod.lessonsDone }}/{{ mod.lessonsTotal }} lessons</span>
            </div>
            <!-- Stacked band bar -->
            <div class="pm-module-bar">
              <template v-if="mod.attempted > 0">
                <div
                  v-for="band in BANDS"
                  :key="band.key"
                  class="pm-module-fill"
                  :style="{
                    width: (mod.bandCounts[band.key] / mod.totalSgs * 100) + '%',
                    background: band.color
                  }"
                ></div>
              </template>
            </div>
            <!-- Band chip counts -->
            <div class="pm-module-band-counts">
              <template v-if="mod.attempted > 0">
                <template v-for="band in BANDS" :key="band.key">
                  <span
                    v-if="mod.bandCounts[band.key] > 0"
                    class="pm-module-band-chip"
                    :style="{ background: band.color + '22', color: band.color }"
                  >{{ mod.bandCounts[band.key] }} {{ band.label }}</span>
                </template>
              </template>
              <span v-else class="pm-mod-badge pm-mod-badge-none">Not started</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="pm-empty-hint">No curriculum data loaded yet.</div>
    </div>

    <!-- ── RECENT ACTIVITY ── -->
    <div class="pm-card">
      <div class="pm-card-header-row">
        <div>
          <h2 class="pm-card-title">Recent Activity</h2>
          <p class="pm-card-sub">{{ childName }}'s learning history</p>
        </div>
      </div>

      <div v-if="groupedActivity.length" class="pm-timeline">
        <template v-for="group in groupedActivity" :key="group.date">
          <div class="pm-tl-date-header">{{ group.label }}</div>
          <div
            v-for="(entry, idx) in group.entries"
            :key="idx"
            class="pm-timeline-item"
          >
            <div class="pm-tl-icon" :class="activityIconClass(entry.type)">
              {{ activityEmoji(entry.type) }}
            </div>
            <div class="pm-tl-body">
              <div class="pm-tl-title">{{ entry.title || activityTypeLabel(entry.type) }}</div>
              <div class="pm-tl-meta">
                <span v-if="entry.score != null" class="pm-tl-score">{{ entry.score }}% score</span>
                <span class="pm-tl-time">{{ formatTime(entry.timestamp) }}</span>
              </div>
            </div>
            <div class="pm-tl-type-badge" :class="activityBadgeClass(entry.type)">
              {{ activityTypeLabel(entry.type) }}
            </div>
          </div>
        </template>
      </div>
      <div v-else class="pm-empty-hint">
        No activity recorded yet. {{ childName }} will appear here once they start learning.
      </div>
    </div>

  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import MTAvatar from '@/components/ui/MTAvatar.vue';

import { useStudentStore } from '@/stores/studentStore';
import { useProgressStore } from '@/stores/progressStore';
import { useCurriculumStore } from '@/stores/curriculumStore';
const route = useRoute();
const router = useRouter();
const childId = route.params.studentId;

const studentStore = useStudentStore();
const progressStore = useProgressStore();
const curriculumStore = useCurriculumStore();

onMounted(async () => {
  await curriculumStore.loadTrack('eplus_core');
  await progressStore.loadProgress(childId);
});

// ── Child info ──────────────────────────────────────────────
const child = computed(() => studentStore.students.find(s => s.id === childId));
const childName = computed(() => child.value?.name || 'Student');
const trackLabel = computed(() => {
  const t = child.value?.track;
  return { eplus_core: '11+ Core', core: '11+ Core', intensive: '11+ Intensive' }[t] || '11+ Core';
});

const lastActiveText = computed(() => {
  const log = progressStore.activityLog[childId] || [];
  if (!log.length) return 'No activity yet';
  const latest = Math.max(...log.map(e => e.timestamp || 0));
  if (!latest) return 'No activity yet';
  const diff = Math.floor((Date.now() - latest) / 86400000);
  if (diff === 0) return 'Active today';
  if (diff === 1) return 'Active yesterday';
  return `Active ${diff} days ago`;
});
const lastActiveClass = computed(() => {
  if (lastActiveText.value === 'Active today') return 'pm-active-today';
  if (lastActiveText.value === 'Active yesterday') return 'pm-active-yesterday';
  return 'pm-active-ago';
});

// ── Hero stats ──────────────────────────────────────────────
const childProg = computed(() => progressStore.getChildProgress(childId) || {});

const SECURE_BANDS = new Set(['secure', 'confident', 'mastery']);

const topicsSecure = computed(() =>
  Object.keys(childProg.value).filter(sgId =>
    SECURE_BANDS.has(progressStore.getSectionGroupBand(childId, sgId))
  ).length
);

const topicsAttempted = computed(() =>
  Object.values(childProg.value).filter(v => (v.attempts || 0) > 0).length
);

const lessonsCompleted = computed(() => {
  if (!curriculumStore.modules?.length) return 0;
  let done = 0;
  for (const mod of curriculumStore.modules) {
    for (const unit of mod.units) {
      for (const lesson of unit.lessons) {
        const subs = lesson.subsections || [];
        if (!subs.length) continue;
        if (subs.every(sub =>
          childProg.value[sub.content] &&
          SECURE_BANDS.has(progressStore.getSectionGroupBand(childId, sub.content))
        )) done++;
      }
    }
  }
  return done;
});

const streak = computed(() => progressStore.dailyStreak?.[childId]?.count ?? 0);

// ── Band distribution ───────────────────────────────────────
const BANDS = [
  { key: 'beginner',   label: 'Beginner',   color: '#3b82f6' },
  { key: 'developing', label: 'Developing', color: '#22c55e' },
  { key: 'secure',     label: 'Secure',     color: '#f59e0b' },
  { key: 'confident',  label: 'Confident',  color: '#f97316' },
  { key: 'mastery',    label: 'Mastery',    color: '#8b5cf6' },
];

const bandCounts = computed(() => {
  const counts = { beginner: 0, developing: 0, secure: 0, confident: 0, mastery: 0 };
  for (const sgId of Object.keys(childProg.value)) {
    const band = progressStore.getSectionGroupBand(childId, sgId);
    counts[band] = (counts[band] || 0) + 1;
  }
  return counts;
});

const totalTrackedTopics = computed(() =>
  Object.values(bandCounts.value).reduce((a, b) => a + b, 0)
);

function bandBarWidth(key) {
  if (!totalTrackedTopics.value) return 0;
  return (bandCounts.value[key] / totalTrackedTopics.value) * 100;
}

function bandColor(key) {
  return BANDS.find(b => b.key === key)?.color || '#94a3b8';
}

function capitalize(str) {
  return str ? str[0].toUpperCase() + str.slice(1) : '';
}

const insightText = computed(() => {
  const { mastery, confident, secure, developing, beginner } = bandCounts.value;
  const strong = (mastery || 0) + (confident || 0);
  const total = totalTrackedTopics.value;
  if (!total) return '';
  const dev = (developing || 0) + (beginner || 0);
  if (strong / total >= 0.5) {
    return dev > 0
      ? `${childName.value} is performing confidently across ${strong} of ${total} topics, with ${dev} still to work on.`
      : `${childName.value} is performing confidently across ${strong} of ${total} tracked topics.`;
  }
  if ((secure || 0) / total >= 0.4) {
    return dev > 0
      ? `${childName.value} is secure in ${(secure || 0) + strong} topics, with ${dev} still developing.`
      : `${childName.value} is secure across all ${total} tracked topics.`;
  }
  if ((developing || 0) >= 1) return `${childName.value} is making progress — ${developing} topics are developing${(beginner || 0) > 0 ? `, ${beginner} are just starting out` : ''}.`;
  return `Skills check recommended to get a full picture of ${childName.value}'s abilities.`;
});

// ── Curriculum name map (sgId → topic & lesson name) ────────
const sgNameMap = computed(() => {
  const map = {};
  for (const mod of (curriculumStore.modules || [])) {
    for (const unit of mod.units) {
      for (const lesson of unit.lessons) {
        for (const sub of (lesson.subsections || [])) {
          map[sub.content] = { name: sub.name, lessonTitle: lesson.title };
        }
      }
    }
  }
  return map;
});

// ── Focus areas (Beginner/Developing topics) ────────────────
const focusAreas = computed(() => {
  const areas = [];
  for (const sgId of Object.keys(childProg.value)) {
    const band = progressStore.getSectionGroupBand(childId, sgId);
    if (band === 'beginner' || band === 'developing') {
      const info = sgNameMap.value[sgId] || {};
      areas.push({ sgId, band, name: info.name || sgId, lessonTitle: info.lessonTitle || '' });
    }
  }
  // Beginner first, then alphabetically
  areas.sort((a, b) => {
    if (a.band !== b.band) return a.band === 'beginner' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  return areas.slice(0, 8);
});

// ── Module progress with band distribution ──────────────────
const moduleColors = [
  { bg: '#dbeafe', fg: '#2563eb' },
  { bg: '#d1fae5', fg: '#059669' },
  { bg: '#ede9fe', fg: '#7c3aed' },
  { bg: '#ffe4e6', fg: '#be123c' },
  { bg: '#fef3c7', fg: '#b45309' },
];
const MODULE_EMOJIS = ['🔢', '📖', '🧩', '🔷', '💡', '✏️'];
function moduleEmoji(i) { return MODULE_EMOJIS[i % MODULE_EMOJIS.length]; }

const moduleProgress = computed(() => {
  if (!curriculumStore.modules?.length) return [];
  return curriculumStore.modules.map(mod => {
    const bc = { beginner: 0, developing: 0, secure: 0, confident: 0, mastery: 0 };
    let totalSgs = 0, attempted = 0, lessonCount = 0, lessonsDone = 0;
    for (const unit of mod.units) {
      for (const lesson of unit.lessons) {
        const subs = lesson.subsections || [];
        if (!subs.length) continue;
        lessonCount++;
        let allSecure = subs.length > 0;
        for (const sub of subs) {
          totalSgs++;
          if (childProg.value[sub.content]) {
            attempted++;
            const band = progressStore.getSectionGroupBand(childId, sub.content);
            bc[band] = (bc[band] || 0) + 1;
            if (!SECURE_BANDS.has(band)) allSecure = false;
          } else {
            allSecure = false;
          }
        }
        if (allSecure) lessonsDone++;
      }
    }
    return { id: mod.id, title: mod.title, bandCounts: bc, totalSgs, attempted, lessonsDone, lessonsTotal: lessonCount };
  });
});

// ── 7-day activity ──────────────────────────────────────────
const weekActivity = computed(() => {
  const log = progressStore.activityLog[childId] || [];
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const dateStr = d.toISOString().slice(0, 10);
    const dayLog = log.filter(e => new Date(e.timestamp).toISOString().slice(0, 10) === dateStr);
    return {
      date: dateStr,
      dayLabel: d.toLocaleDateString('en-GB', { weekday: 'short' }),
      dayNum: d.getDate(),
      count: dayLog.length,
      hasLesson:     dayLog.some(e => e.type === 'lesson' || e.type === 'continue' || e.type === 'newLesson'),
      hasRevision:   dayLog.some(e => e.type === 'revision'),
      hasDiagnostic: dayLog.some(e => e.type === 'diagnostic'),
    };
  });
});

function startOfWeekMs() {
  const d = new Date();
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

const thisWeekSessions = computed(() => {
  const wStart = startOfWeekMs();
  return (progressStore.activityLog[childId] || []).filter(e => (e.timestamp || 0) >= wStart).length;
});

const thisWeekLessons = computed(() => {
  const wStart = startOfWeekMs();
  return (progressStore.activityLog[childId] || []).filter(
    e => (e.timestamp || 0) >= wStart && (e.type === 'lesson' || e.type === 'newLesson')
  ).length;
});

// ── Topics Secure+ trend ────────────────────────────────────
// Count Secure+ topics whose lastAttempt falls within a time window.
function secureTopicsInWindow(fromMs, toMs) {
  return Object.entries(childProg.value).filter(([sgId, p]) => {
    if (!SECURE_BANDS.has(progressStore.getSectionGroupBand(childId, sgId))) return false;
    const la = p.lastAttempt || 0;
    return la >= fromMs && la < toMs;
  }).length;
}

// Rolling windows — avoids artificial drops at Mon week boundary
const secureThisWeek = computed(() => {
  const now = Date.now();
  return secureTopicsInWindow(now - 7 * 86400000, now + 1);
});

const secureLastWeek = computed(() => {
  const now = Date.now();
  return secureTopicsInWindow(now - 14 * 86400000, now - 7 * 86400000);
});

// Topics that reached/reinforced Secure+ in the last 7 rolling days
// (used for the hero pill — more intuitive than Mon–Sun week)
const secureGainThisWeek = computed(() => {
  const sevenDaysAgo = Date.now() - 7 * 86400000;
  return secureTopicsInWindow(sevenDaysAgo, Date.now() + 1);
});

const secureTrendDisplay = computed(() => {
  const curr = secureThisWeek.value;
  const prev = secureLastWeek.value;
  if (curr === 0 && prev === 0) return '—';
  const diff = curr - prev;
  if (diff === 0) return '=';
  return (diff > 0 ? '+' : '') + diff;
});

const secureTrendClass = computed(() => {
  const diff = secureThisWeek.value - secureLastWeek.value;
  if (diff > 0) return 'pm-trend-up';
  if (diff < 0) return 'pm-trend-down';
  return 'pm-trend-neutral';
});

// 4-week bar chart data
const weeklyTrend = computed(() => {
  const now = Date.now();
  const MS_PER_WEEK = 7 * 86400000;
  const weeks = Array.from({ length: 4 }, (_, i) => {
    const toMs   = now - i * MS_PER_WEEK;
    const fromMs = toMs - MS_PER_WEEK;
    const isCurrent = i === 0;
    const label = isCurrent ? 'This week'
      : i === 1 ? 'Last week'
      : (() => { const d = new Date(fromMs); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); })();
    return { label, count: secureTopicsInWindow(fromMs, toMs), isCurrent };
  }).reverse();

  const max = Math.max(...weeks.map(w => w.count), 1);
  return weeks.map(w => ({ ...w, heightPct: Math.round((w.count / max) * 100) }));
});

const overallTrendText = computed(() => {
  const [oldest, , , newest] = weeklyTrend.value;
  if (!oldest || !newest) return '';
  const diff = newest.count - oldest.count;
  if (diff > 0) return `↑ Up ${diff} topics vs 4 weeks ago`;
  if (diff < 0) return `↓ Down ${Math.abs(diff)} topics vs 4 weeks ago`;
  return '';
});

const overallTrendClass = computed(() => {
  const [oldest, , , newest] = weeklyTrend.value;
  if (!oldest || !newest) return '';
  return newest.count >= oldest.count ? 'pm-trend-up' : 'pm-trend-down';
});

// ── Recent activity — grouped by day ────────────────────────
const groupedActivity = computed(() => {
  const log = progressStore.activityLog[childId] || [];
  const sorted = [...log].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)).slice(0, 30);

  const groups = [];
  let currentDate = null;
  for (const entry of sorted) {
    const dateStr = entry.timestamp
      ? new Date(entry.timestamp).toISOString().slice(0, 10)
      : 'unknown';
    if (dateStr !== currentDate) {
      currentDate = dateStr;
      groups.push({ date: dateStr, label: formatDateLabel(dateStr), entries: [] });
    }
    groups[groups.length - 1].entries.push(entry);
  }
  return groups;
});

function formatDateLabel(dateStr) {
  if (!dateStr || dateStr === 'unknown') return 'Unknown date';
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  if (d.getTime() === today.getTime()) return 'Today';
  if (d.getTime() === yesterday.getTime()) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function activityEmoji(type) {
  return { lesson: '📚', newLesson: '📚', continue: '📚', revision: '🧠', diagnostic: '🔬', test: '📝', badge: '🏅' }[type] || '⭐';
}
function activityTypeLabel(type) {
  return { lesson: 'Lesson', newLesson: 'Lesson', continue: 'Lesson', revision: 'Revision', diagnostic: 'Skills Check', test: 'Test', badge: 'Badge' }[type] || 'Activity';
}
function activityIconClass(type) {
  return { lesson: 'pm-tl-icon-blue', newLesson: 'pm-tl-icon-blue', continue: 'pm-tl-icon-blue', revision: 'pm-tl-icon-purple', diagnostic: 'pm-tl-icon-amber', test: 'pm-tl-icon-green', badge: 'pm-tl-icon-orange' }[type] || 'pm-tl-icon-blue';
}
function activityBadgeClass(type) {
  return { lesson: 'pm-badge-blue', newLesson: 'pm-badge-blue', continue: 'pm-badge-blue', revision: 'pm-badge-purple', diagnostic: 'pm-badge-amber', test: 'pm-badge-green', badge: 'pm-badge-orange' }[type] || 'pm-badge-blue';
}

// ── Actions ─────────────────────────────────────────────────
function openStudentView() {
  router.push(`/student/${childId}/dashboard`);
}
</script>

<style scoped>
.pm-root {
  width: 100%;
}

/* ── Header ── */
.pm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
}
.pm-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}
.pm-back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.pm-back-btn:hover { border-color: #CBD5E1; color: #475569; background: #F8FAFC; }

.pm-header-child {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pm-child-name {
  font-size: 22px;
  font-weight: 800;
  color: #1A2332;
  line-height: 1.2;
}
.pm-child-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.pm-track-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  background: #DBEAFE;
  color: #2563EB;
}
.pm-last-active { font-size: 12px; font-weight: 600; }
.pm-active-today { color: #15803d; }
.pm-active-yesterday { color: #92400e; }
.pm-active-ago { color: #94a3b8; }

.pm-open-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #1A2332;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 11px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  flex-shrink: 0;
}
.pm-open-btn:hover { background: #2d3748; }

/* ── Hero stats ── */
.pm-hero {
  background: white;
  border-radius: 14px;
  border: 1px solid #EEE;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04);
  padding: 20px 28px;
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.pm-hero-stat {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 130px;
  padding: 8px 0;
}
.pm-hero-divider {
  width: 1px;
  height: 48px;
  background: #F1F5F9;
  margin: 0 4px;
  flex-shrink: 0;
}
.pm-hero-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pm-icon-amber  { background: #FEF3C7; color: #B45309; }
.pm-icon-green  { background: #D1FAE5; color: #059669; }
.pm-icon-blue   { background: #DBEAFE; color: #2563EB; }
.pm-icon-orange { background: #FFEDD5; color: #EA580C; }
.pm-icon-purple { background: #EDE9FE; color: #7C3AED; }

.pm-hero-val {
  font-size: 22px;
  font-weight: 800;
  color: #1A2332;
  line-height: 1;
}
.pm-hero-lbl {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 3px;
}

/* ── Grid 2-col ── */
.pm-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

/* ── Cards ── */
.pm-card {
  background: white;
  border-radius: 14px;
  border: 1px solid #EEE;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04);
  padding: 22px 24px;
}
.pm-upgrade-block {
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(135deg, #1A2332 0%, #2d3f57 100%);
  border-radius: 14px;
  padding: 18px 24px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.pm-upgrade-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(245,158,11,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.pm-upgrade-title { font-size: 14px; font-weight: 700; color: white; margin-bottom: 3px; }
.pm-upgrade-desc  { font-size: 12px; color: rgba(255,255,255,0.6); max-width: 460px; }
.pm-upgrade-btn {
  margin-left: auto;
  flex-shrink: 0;
  background: #F59E0B;
  color: #1A2332;
  font-size: 13px;
  font-weight: 800;
  padding: 10px 20px;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.15s;
  white-space: nowrap;
}
.pm-upgrade-btn:hover { background: #fbbf24; }

.pm-card-title {
  font-size: 16px;
  font-weight: 800;
  color: #1A2332;
  margin-bottom: 3px;
}
.pm-card-sub {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 20px;
}
.pm-card-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0;
}

/* ── Band distribution ── */
.pm-band-bar-wrap { margin-bottom: 16px; }
.pm-band-bar {
  height: 12px;
  border-radius: 99px;
  background: #F1F5F9;
  overflow: hidden;
  display: flex;
}
.pm-band-segment {
  height: 100%;
  transition: width 0.4s ease;
}
.pm-band-total {
  font-size: 11px;
  color: #CBD5E1;
  margin-top: 5px;
  text-align: right;
  font-weight: 500;
}
.pm-band-legend {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.pm-band-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #F8FAFC;
  border-radius: 8px;
  padding: 8px 12px;
  flex: 1;
  min-width: 80px;
}
.pm-band-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.pm-band-count { font-size: 18px; font-weight: 800; color: #1A2332; line-height: 1; }
.pm-band-name  { font-size: 10px; color: #94a3b8; font-weight: 500; }

.pm-insight {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: #15803d;
  font-weight: 500;
  line-height: 1.5;
}

/* ── Weekly activity ── */
.pm-week-grid {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
}
.pm-day-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.pm-day-label { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; }
.pm-day-num   { font-size: 13px; font-weight: 700; color: #1A2332; }
.pm-day-dots {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 44px;
  align-items: center;
  justify-content: center;
}
.pm-day-dot { width: 10px; height: 10px; border-radius: 50%; }
.pm-dot-lesson     { background: #3b82f6; }
.pm-dot-revision   { background: #8b5cf6; }
.pm-dot-diagnostic { background: #f59e0b; }
.pm-dot-none       { background: #F1F5F9; }
.pm-day-count        { font-size: 11px; color: #CBD5E1; font-weight: 600; }
.pm-day-count-active { color: #64748b; }

.pm-week-legend {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.pm-wleg-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
}
.pm-wleg-dot { width: 8px; height: 8px; border-radius: 50%; }

.pm-week-summary {
  display: flex;
  align-items: center;
  background: #F8FAFC;
  border-radius: 10px;
  padding: 12px 16px;
  gap: 0;
}
.pm-wsum-item { flex: 1; text-align: center; }
.pm-wsum-val  { font-size: 20px; font-weight: 800; color: #1A2332; display: block; line-height: 1; }
.pm-wsum-lbl  { font-size: 11px; color: #94a3b8; font-weight: 500; display: block; margin-top: 3px; }
.pm-wsum-sep  { width: 1px; height: 36px; background: #E2E8F0; margin: 0 4px; }

.pm-trend-up      { color: #15803d !important; }
.pm-trend-down    { color: #be123c !important; }
.pm-trend-neutral { color: #94a3b8 !important; }

/* Hero trend pill */
.pm-hero-val-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.pm-hero-trend-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  background: #D1FAE5;
  color: #15803d;
  white-space: nowrap;
}

/* Progress trend bars */
.pm-trend-weeks {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  height: 120px;
  margin-bottom: 10px;
}
.pm-trend-week-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  height: 100%;
  justify-content: flex-end;
}
.pm-trend-bar-wrap {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: flex-end;
  min-height: 4px;
}
.pm-trend-bar-fill {
  width: 100%;
  min-height: 4px;
  border-radius: 6px 6px 0 0;
  transition: height 0.4s ease;
}
.pm-trend-bar-past    { background: #E2E8F0; }
.pm-trend-bar-current { background: #8b5cf6; }

.pm-trend-count {
  font-size: 14px;
  font-weight: 800;
  color: #64748b;
}
.pm-trend-count-current { color: #7c3aed; }

.pm-trend-label {
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-align: center;
  white-space: nowrap;
}
.pm-trend-footnote {
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.5;
  margin-top: 8px;
  border-top: 1px solid #F1F5F9;
  padding-top: 10px;
}

/* ── Focus areas ── */
.pm-focus-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pm-focus-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #F8FAFC;
  border-radius: 10px;
  padding: 10px 14px;
  transition: background 0.1s;
}
.pm-focus-item:hover { background: #F1F5F9; }
.pm-focus-band-strip {
  width: 4px;
  height: 32px;
  border-radius: 2px;
  flex-shrink: 0;
}
.pm-focus-body { flex: 1; min-width: 0; }
.pm-focus-name   { font-size: 13px; font-weight: 700; color: #1A2332; }
.pm-focus-lesson { font-size: 11px; color: #94a3b8; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pm-focus-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  flex-shrink: 0;
}
.pm-focus-badge-beginner  { background: #DBEAFE; color: #2563EB; }
.pm-focus-badge-developing { background: #D1FAE5; color: #059669; }

/* ── Module progress ── */
.pm-modules { display: flex; flex-direction: column; gap: 16px; }
.pm-module-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.pm-module-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 2px;
}
.pm-module-body { flex: 1; }
.pm-module-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.pm-module-name    { font-size: 14px; font-weight: 700; color: #1A2332; }
.pm-module-lessons { font-size: 12px; color: #94a3b8; font-weight: 500; }

.pm-module-bar {
  height: 8px;
  border-radius: 99px;
  background: #F1F5F9;
  overflow: hidden;
  margin-bottom: 8px;
  display: flex;
}
.pm-module-fill {
  height: 100%;
  transition: width 0.4s ease;
}

.pm-module-band-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.pm-module-band-chip {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
}

.pm-module-band-row { display: flex; gap: 6px; }
.pm-mod-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
}
.pm-mod-badge-none { background: #F1F5F9; color: #94a3b8; }

/* ── Timeline ── */
.pm-timeline { display: flex; flex-direction: column; gap: 2px; margin-top: 8px; }

.pm-tl-date-header {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 12px 12px 4px;
}
.pm-tl-date-header:first-child { padding-top: 2px; }

.pm-timeline-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  transition: background 0.1s;
}
.pm-timeline-item:hover { background: #F8FAFC; }

.pm-tl-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}
.pm-tl-icon-blue   { background: #DBEAFE; }
.pm-tl-icon-purple { background: #EDE9FE; }
.pm-tl-icon-amber  { background: #FEF3C7; }
.pm-tl-icon-green  { background: #D1FAE5; }
.pm-tl-icon-orange { background: #FFEDD5; }

.pm-tl-body { flex: 1; min-width: 0; }
.pm-tl-title { font-size: 13px; font-weight: 600; color: #1A2332; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pm-tl-meta  { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
.pm-tl-score { font-size: 11px; font-weight: 700; color: #059669; }
.pm-tl-time  { font-size: 11px; color: #94a3b8; font-weight: 500; }

.pm-tl-type-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  flex-shrink: 0;
}
.pm-badge-blue   { background: #DBEAFE; color: #2563EB; }
.pm-badge-purple { background: #EDE9FE; color: #7C3AED; }
.pm-badge-amber  { background: #FEF3C7; color: #B45309; }
.pm-badge-green  { background: #D1FAE5; color: #059669; }
.pm-badge-orange { background: #FFEDD5; color: #EA580C; }

/* ── Empty hint ── */
.pm-empty-hint {
  font-size: 13px;
  color: #94a3b8;
  padding: 16px 0;
  text-align: center;
}

/* ── Responsive ── */
@media (max-width: 700px) {
  .pm-hero {
    gap: 0;
    padding: 16px;
  }
  .pm-hero-divider { display: none; }
  .pm-hero-stat {
    min-width: 40%;
    padding: 10px 8px;
  }
  .pm-grid-2 {
    grid-template-columns: 1fr;
  }
  .pm-week-grid {
    gap: 4px;
  }
}
</style>
